import React, { useState, useEffect } from 'react'
import customSelect from 'custom-select'
import { SimulatorFuelOutputPrice } from '~/components/slices/simulatorFuel/SimulatorFuelOutputPrice'
import { SimulatorFuelOutputDistances } from '~/components/slices/simulatorFuel/SimulatorFuelOutputDistances'
import { SimulatorFuelInput } from '~/components/slices/simulatorFuel/SimulatorFuelInput'
import { SimulatorFuelMoreTables } from '~/components/slices/simulatorFuel/SimulatorFuelMoreTables'
import { calculateOutput } from '~/components/slices/simulatorFuel/utils'
import { extractJson } from '~/utils/extractJson'
import Wysiwyg from '~/components/core/Wysiwyg/Wysiwyg'
import { simulatorData } from '~/components/slices/simulatorFuel/simulatorData'
import { useSelector } from '~/store/hooks'

type SimulatorFuelProps = {
  data: Queries.SliceSimulatorFuelDataFragment
}

const SimulatorFuel = ({ data: contentfulData }: SimulatorFuelProps) => {
  const lang = useSelector((state) => state.lang)

  // There's a bug in Contentful json fields so we have to harcode some json values

  const contentfulDataValid = {
    ...contentfulData,
    fuelDataGlobalConfiguration: simulatorData({ lang: lang })
      .fuelDataGlobalConfiguration,
    section1Block3Tables: extractJson(contentfulData.section1Block3Tables),
    section1PriceInputRange: extractJson(
      contentfulData.section1PriceInputRange,
    ),
    section1Block3VehicleCategories: simulatorData({ lang: lang })
      .section1Block3VehicleCategories,
    section3TablesOrganization: extractJson(
      contentfulData.section3TablesOrganization,
    )?.data,
  }

  const [fuelType, setFuelType] = useState('B7')
  const [price, setPrice] = useState(1.9)
  const [output, setOutput] = useState([])
  const [equiv, setEquiv] = useState(0)
  const [alertMessage, setAlertMessage] = useState('')
  const [distances, setDistances] = useState({
    VL: 0,
    VUL: 0,
    CAR: 0,
    PL: 0,
  })

  const {
    section1PriceInputRange,
    fuelDataGlobalConfiguration,
    section1Block3Tables,
    section3TablesOrganization,
    section1Block3VehicleCategories,
  } = contentfulDataValid

  useEffect(() => {
    customSelect('#mySelect')
  }, [])

  useEffect(() => {
    if (section1PriceInputRange && fuelDataGlobalConfiguration) {
      handleCalculate()
    }
  }, [lang, price, fuelType])

  const handleCalculate = () => {
    if (!section1PriceInputRange) {
      return null
    }
    if (
      price !== 0 &&
      (price < section1PriceInputRange.min ||
        price > section1PriceInputRange.max)
    ) {
      setAlertMessage(
        `Le prix du carburant doit être compris entre ${section1PriceInputRange.min} et ${section1PriceInputRange.max}`,
      )
      return
    }
    setAlertMessage('')
    calculateOutput(
      contentfulDataValid,
      fuelType,
      price,
      setAlertMessage,
      setOutput,
      setEquiv,
      setDistances,
      fuelType,
      lang,
    )
  }

  useEffect(() => {
    const selectElement = document.querySelector('#mySelect')
    customSelect(selectElement)

    if (selectElement) {
      const handleSelectChange = (event) => {
        setFuelType(event.target.value)
      }

      selectElement.addEventListener('change', handleSelectChange)

      return () => {
        selectElement.removeEventListener('change', handleSelectChange)
      }
    }
  }, [])

  const shouldRender =
    !!fuelDataGlobalConfiguration &&
    !!section1Block3Tables &&
    !!section3TablesOrganization &&
    !!section1PriceInputRange &&
    !!section1Block3VehicleCategories

  if (!shouldRender) {
    return null
  }

  const options = Object.entries(fuelDataGlobalConfiguration).map(
    ([key, { lib }]) => ({
      value: key,
      label: lib,
    }),
  )

  const passThroughData = {
    fuelType,
    setFuelType,
    price,
    setPrice,
    distances,
    setDistances,
    output,
    equiv,
    alertMessage,
    setAlertMessage,
    options,
    contentfulData: contentfulDataValid,
  }

  return (
    <div className="simulator-fuel">
      <div className="simulator">
        <div className="fluid-grid simulator-fuel-section-1 max-w-[1200px]">
          <div className="pb-10 md:pb-0 row md:relative md:bottom-8">
            <div className="col-12 lg-col-10 xl-col-8 mx-auto">
              <div className="border-grey border-[1px]">
                <div className="flex w-full flex-col md:flex-row">
                  <SimulatorFuelInput {...passThroughData} />
                  <SimulatorFuelOutputPrice {...passThroughData} />
                </div>
                <SimulatorFuelOutputDistances {...passThroughData} />
              </div>
            </div>
          </div>
        </div>
        <div className="fluid-grid">
          <div className="row pb-16 md:pb-20">
            <div className="col-12 lg-col-10 xl-col-8 mx-auto wysiwyg">
              <Wysiwyg document={contentfulData.section2} />
            </div>
          </div>
        </div>
        <div className="fluid-grid simulator-fuel-section-2">
          <div className="row pb-16 md:pb-20">
            <div className="col-12 lg-col-11 xl-col-9 mx-auto">
              <SimulatorFuelMoreTables {...passThroughData} />
              <p className="text-center mt-12">
                {contentfulData.section3FooterText}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SimulatorFuel
